import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {pluralize} from 'BenchlingUtil';
import {PreviewConstruct} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import {ConstructModalTab} from 'molbio/bulk-assembly/polymer-map-modals/ConstructModal';
import PolymerPreview from 'molbio/components/visualizations/PolymerPreview';
import {VisualPolymer} from 'molbio/components/visualizations/PolymerPreviewConstants';
import {FONT_PROXIMA_11, FONT_PROXIMA_13, FONT_WEIGHT_PROXIMA_REGULAR} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonStyle} from 'ui-library/buttons/Button';
import {BioentityChip} from 'ui-library/chips/BioentityChip';
import {ChipSize} from 'ui-library/chips/Chip';
import {GRAY, GRAY_DARKER, ORANGE, RED_DARK} from 'ui-library/Colors';
import Popover from 'ui-library/floating-ui/Popover';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

const CONSTRUCT_PREVIEW_CARD_SIZE_PX = 236;

function ErrorIcon(): JSX.Element {
  return (
    <div className={css(styles.iconWrapper)}>
      <Popover
        renderContent={() => (
          <span>
            This construct is invalid. It cannot be assembled as currently designed. To review the relevant
            errors, go to the <strong>Overview</strong> tab.
          </span>
        )}
      >
        <BenchlingIcon icon="revision" className={css(styles.icon, styles.errorIcon)} />
      </Popover>
    </div>
  );
}

function WarningIcon(): JSX.Element {
  return (
    <div className={css(styles.iconWrapper)}>
      <Popover
        renderContent={() => (
          <span>
            This construct has associated warnings. To review the relevant warnings, go to the{' '}
            <strong>Overview</strong> tab.
          </span>
        )}
      >
        <BenchlingIcon icon="warning" className={css(styles.icon, styles.warningIcon)} />
      </Popover>
    </div>
  );
}

function PolymerPreviewSVG({
  visualPolymer,
  uniquePrefix,
  onClick,
}: {
  visualPolymer: VisualPolymer | null;
  uniquePrefix: string;
  onClick: () => void;
}): JSX.Element {
  const circular = visualPolymer?.circular;
  return (
    <div
      // Use circular styling when there is an error.
      className={css(circular || !visualPolymer ? styles.previewSVGPlasmid : styles.previewSVGLinear)}
      onClick={onClick}
      {...getTestProps({component: 'ConstructPreviewCard', element: 'preview'})}
    >
      {visualPolymer && (
        <div {...getTestProps({component: 'ConstructPreviewCard', element: 'polymerPreview'})}>
          <PolymerPreview uniqueIdPrefix={uniquePrefix} polymer={visualPolymer} />
        </div>
      )}
    </div>
  );
}

interface ConstructPreviewCardProps {
  construct: PreviewConstruct;
  canCreatePrimers: boolean;
  openConstructModal: (initialTab: ConstructModalTab) => Promise<void>;
}

export default function ConstructPreviewCard({
  construct,
  canCreatePrimers,
  openConstructModal,
}: ConstructPreviewCardProps): JSX.Element {
  const {name, primers, hasErrors, hasWarnings, visualPolymer, outputPolymerId, cid} = construct;
  const numPrimers = primers.length;

  return (
    <div
      className={css(styles.cardContainer)}
      {...getTestProps({component: 'ConstructPreviewCard', element: 'root'})}
    >
      <div className={css(styles.cardHeader)}>
        {hasErrors ? <ErrorIcon /> : hasWarnings ? <WarningIcon /> : null}
        {outputPolymerId != null ? (
          <div {...getTestProps({component: 'ConstructPreviewCard', element: 'name'})}>
            <BioentityChip id={outputPolymerId} size={ChipSize.MINI} validationExtras={null} />
          </div>
        ) : (
          <div
            className={css(styles.polymerName)}
            {...getTestProps({component: 'ConstructPreviewCard', element: 'name'})}
          >
            {name}
          </div>
        )}
        <BenchlingIcon
          className={css(styles.expandIcon)}
          dataTestAttrs={{component: 'ConstructPreviewCard', element: 'expandIcon'}}
          icon="expand-right-full"
          onClick={() => {
            openConstructModal(ConstructModalTab.POLYMER);
          }}
        />
      </div>
      <PolymerPreviewSVG
        visualPolymer={visualPolymer}
        uniquePrefix={cid}
        onClick={() => {
          openConstructModal(ConstructModalTab.POLYMER);
        }}
      />
      {numPrimers > 0 && (
        <div className={css(styles.primerViewFooter)}>
          <p className={css(styles.primerViewText)}>{`${numPrimers} associated ${pluralize(
            numPrimers,
            'primer'
          )}`}</p>
          <Button
            className={css(styles.primerViewLink)}
            buttonStyle={ButtonStyle.LINK}
            disabled={false}
            onClick={(e) => {
              e.stopPropagation();
              openConstructModal(ConstructModalTab.PRIMERS);
            }}
            text="View"
          />
        </div>
      )}
      {numPrimers === 0 && canCreatePrimers && (
        <p className={css(styles.primerViewText)}>No associated primers</p>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  cardContainer: {
    border: `1px solid ${GRAY}`,
    borderRadius: '4px',
    padding: '12px',
    minWidth: '266px',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 6,
    gap: '4px',
  },
  polymerName: {
    ...FONT_PROXIMA_13,
    marginBottom: 0,
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  // The icon size needs to be set on both the div wrapper and the icon itself in order for the
  // icon and layout to look right.
  iconWrapper: {
    height: 16,
    width: 16,
  },
  icon: {
    height: 16,
    width: 16,
  },
  errorIcon: {
    color: RED_DARK,
  },
  warningIcon: {
    color: ORANGE,
  },
  previewSVGPlasmid: {
    margin: '4px auto',
    width: CONSTRUCT_PREVIEW_CARD_SIZE_PX,
    height: CONSTRUCT_PREVIEW_CARD_SIZE_PX,
    cursor: 'pointer',
  },
  previewSVGLinear: {
    margin: '4px auto',
    minWidth: CONSTRUCT_PREVIEW_CARD_SIZE_PX,
    maxWidth: '350px',
    // May need to adjust this to accommodate max # of annotation rows + space for cutsites
    height: '200px',
    cursor: 'pointer',
  },
  expandIcon: {
    color: GRAY_DARKER,
    marginTop: '2px',
    cursor: 'pointer',
  },
  primerViewFooter: {
    display: 'flex',
  },
  primerViewText: {
    ...FONT_PROXIMA_11,
    marginRight: '8px',
    marginBottom: '0',
  },
  primerViewLink: {
    ...FONT_PROXIMA_11,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
  },
});
